<template>
    <PageHeader :title="t('my.accountDetails')" />
    <div class="page-wrapper" style="padding-top: calc(50px * 1.1)!important;">
        <div class="details-container desktop-container">
            <el-scrollbar
                ref="scrollbarRef"
                class="record-list"
                v-loading="loading"
                :element-loading-text="t('details.loading')"
                @scroll="handleScroll"
            >
                <div v-if="!loading && records.length === 0" class="empty-state">
                    <div class="empty-icon">📋</div>
                    <div class="empty-text">{{ t('details.noRecords') }}</div>
                </div>

                <div v-for="record in records" :key="record.id" class="record-item">
                    <div class="record-header">
                        <div class="record-time"><span class="label">{{ t('details.time') }}</span><span class="value">{{ record.time }}</span></div>
                    </div>
                    <div class="record-details">
                        <div class="detail-row"><span class="label">{{ t('details.type') }}</span><span class="value type" :class="'type-' + record.type">{{ record.typeText }}</span></div>
                        <div class="detail-row"><span class="label">{{ t('details.amount') }}</span><span class="value" :class="{ 'positive': record.amount > 0, 'negative': record.amount < 0 }">{{ appConfig.currency.symbol }}{{ record.amount }}</span></div>
                    </div>
                </div>

                <div v-if="!loading && records.length > 0 && noMore" class="no-more-section">
                    <div class="no-more-text">{{ t('details.noMore') }}</div>
                </div>
            </el-scrollbar>
            <el-backtop :bottom="30" target=".record-list .el-scrollbar__wrap" />
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { getBalanceLog } from '@/api'
import { appConfig } from '@/config/app'
import PageHeader from '@/components/PageHeader.vue'

const router = useRouter()
const { t } = useI18n()

const loading = ref(false)
const noMore = ref(false)
const currentPage = ref(1)
const pageSize = 10
const scrollbarRef = ref(null)
const records = ref([])

const typeMap = computed(() => (type) => {
  const map = {
    0: t('details.type0'),
    1: t('details.type1'),
    2: t('details.type2'),
    3: t('details.type3'),
    4: t('details.type4'),
    5: t('details.type5'),
    8: t('details.type8')
  }
  return map[type] || t('details.type0')
})

const fetchRecords = async (page = 1, isLoadMore = false) => {
  if (!isLoadMore) {
    records.value = []
    currentPage.value = 1
    noMore.value = false
  }

  loading.value = true

  try {
    const response = await getBalanceLog({ page, limit: pageSize, type: 0 })
    if (response.data.code === 200) {
      const processedData = response.data.data.list.map(item => ({
        id: item.id,
        time: item.addtime,
        type: item.type,
        typeText: typeMap.value(item.type),
        amount: item.num,
        balance: ''
      }))

      if (isLoadMore) {
        records.value.push(...processedData)
        currentPage.value = page
      } else {
        records.value = processedData
      }

      noMore.value = response.data.data.list.length < pageSize
      currentPage.value = parseInt(response.data.data.page) || 1
    }
  } catch (error) {
    console.error('获取余额明细失败:', error)
  } finally {
    loading.value = false
  }
}

const handleScroll = ({ scrollTop }) => {
  const wrap = scrollbarRef.value?.wrapRef
  if (!wrap) return

  const { scrollHeight, clientHeight } = wrap
  const distance = scrollHeight - scrollTop - clientHeight

  if (distance < 50 && !loading.value && !noMore.value) {
    console.log('下拉到第', currentPage.value + 1, '页')
    loadMore()
  }
}

const loadMore = () => {
  fetchRecords(currentPage.value + 1, true)
}

onMounted(() => {
  fetchRecords(1, false)
})
</script>

<style scoped>
.details-container {
    background: var(--bg-secondary);
    height: calc(100vh - 54px);
    overflow: hidden;
    padding: 10px
}

.record-list {
    height: calc(100vh - 54px);
    position: relative
}

.record-list .record-item {
    background: var(--card-bg);
    border-radius: 12px;
    margin-bottom: 10px;
    padding: 15px;
    box-shadow: var(--shadow-base);
    transition: all .3s ease
}

.record-list .record-item:hover {
    box-shadow: 0 4px 15px #0000001f;
    transform: translateY(-2px)
}

.record-list .record-item .record-header {
    border-bottom: 1px solid var(--border-color);
    padding-bottom: 10px;
    margin-bottom: 10px
}

.record-list .record-item .record-header .record-time {
    display: flex;
    align-items: center;
    gap: 8px
}

.record-list .record-item .record-header .record-time .label {
    font-size: 13px;
    color: var(--text-secondary);
    font-weight: 500
}

.record-list .record-item .record-header .record-time .value {
    font-size: 14px;
    color: var(--text-primary);
    font-weight: 600;
    font-family: Courier New,monospace
}

.record-list .record-item .record-details .detail-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px
}

.record-list .record-item .record-details .detail-row:last-child {
    margin-bottom: 0
}

.record-list .record-item .record-details .detail-row .label {
    font-size: 14px;
    color: var(--text-secondary);
    font-weight: 500
}

.record-list .record-item .record-details .detail-row .value {
    font-size: 14px;
    color: var(--text-primary);
    font-weight: 600
}

.record-list .record-item .record-details .detail-row .value.type {
    padding:4px 8px;
    border-radius: 10px;
    font-size: 12px
}

.record-list .record-item .record-details .detail-row .value.type.type-0 {
    background: #6c757d1a;
    color: #6c757d
}

.record-list .record-item .record-details .detail-row .value.type.type-1 {
    background: #28a7451a;
    color: #28a745
}

.record-list .record-item .record-details .detail-row .value.type.type-2 {
    background: #dc35451a;
    color: #dc3545
}

.record-list .record-item .record-details .detail-row .value.type.type-3 {
    background: #17a2b81a;
    color: #17a2b8
}

.record-list .record-item .record-details .detail-row .value.type.type-4 {
    background: #ffc1071a;
    color: #ff6b35
}

.record-list .record-item .record-details .detail-row .value.type.type-5 {
    background: #667eea1a;
    color: #667eea
}

.record-list .record-item .record-details .detail-row .value.positive {
    color: #28a745
}

.record-list .record-item .record-details .detail-row .value.negative {
    color: #dc3545
}

.record-list .record-item .record-details .detail-row .value.remark {
    font-size: 13px;
    color: var(--text-secondary);
    font-weight: 400
}

.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 30px
}

.empty-state .empty-icon {
    font-size: 60px;
    margin-bottom: 15px;
    opacity: .6
}

.empty-state .empty-text {
    font-size: 14px;
    color: var(--text-secondary);
    text-align: center;
    line-height: 1.6
}

.no-more-section {
    padding: 16px;
    text-align: center;
    color: #999
}

.no-more-section .no-more-text {
    font-size: 12px
}

:deep(.el-scrollbar__bar) {
    display: none
}

.el-loading-mask {
    display: flex !important
}

@media screen and (min-width: 768px) {
    .details-container {
        padding:15px
    }

    .record-list .record-item {
        padding: 20px
    }

    .record-list .record-item .record-header {
        padding-bottom: 12px;
        margin-bottom: 12px
    }

    .record-list .record-item .record-details .detail-row {
        margin-bottom: 10px
    }
}
</style>
